import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { Ub04Form, Ub04Service, createEmptyUB04Service } from '../../services/ub04Service';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1),
  },
  fieldLabel: {
    fontWeight: 'bold',
    fontSize: '0.8rem',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[200],
    fontWeight: 'bold',
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
  },
}));

interface UB04FormProps {
  formData: Ub04Form;
  onFormChange: (formData: Ub04Form) => void;
  onSave: () => void;
  onAddForm?: () => void;
}

// Helper function to format date to MM/DD/YYYY
const formatDate = (date: Date | null): string => {
  if (!date) return '';
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
};

// Helper function to parse date string to Date object
const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  // Check if the string matches MM/DD/YYYY format
  const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = dateString.match(regex);
  
  if (!match) return null;
  
  const month = parseInt(match[1], 10) - 1; // Months are 0-indexed in JS
  const day = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  
  const date = new Date(year, month, day);
  
  // Validate the date is valid
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }
  
  return date;
};

const UB04FormComponent: React.FC<UB04FormProps> = ({ formData, onFormChange, onSave, onAddForm }) => {
  const classes = useStyles();
  const [form, setForm] = useState<Ub04Form>(formData);

  useEffect(() => {
    setForm(formData);
  }, [formData]);

  const handleInputChange = (field: keyof Ub04Form, value: any) => {
    const updatedForm = {
      ...form,
      [field]: value,
      isDirty: true,
    };
    setForm(updatedForm);
    onFormChange(updatedForm);
  };

  const handleServiceChange = (index: number, field: keyof Ub04Service, value: any) => {
    const updatedServices = [...form.services];
    updatedServices[index] = {
      ...updatedServices[index],
      [field]: value,
      isDirty: true,
    };

    const updatedForm = {
      ...form,
      services: updatedServices,
      isDirty: true,
    };
    
    setForm(updatedForm);
    onFormChange(updatedForm);
  };

  const handleAddService = () => {
    const updatedForm = {
      ...form,
      services: [...form.services, createEmptyUB04Service()],
      isDirty: true,
    };
    setForm(updatedForm);
    onFormChange(updatedForm);
  };

  const handleDeleteService = (index: number) => {
    const updatedServices = [...form.services];
    updatedServices.splice(index, 1);

    const updatedForm = {
      ...form,
      services: updatedServices,
      isDirty: true,
    };
    
    setForm(updatedForm);
    onFormChange(updatedForm);
  };

  return (
    <Paper className={classes.root}>
        <Box className={classes.formSection}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Provider Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Billing Provider Name"
                fullWidth
                value={form.billingProviderName}
                onChange={(e) => handleInputChange('billingProviderName', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Patient Control Number"
                fullWidth
                value={form.patientControlNumber}
                onChange={(e) => handleInputChange('patientControlNumber', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Billing Provider Address"
                fullWidth
                value={form.billingProviderAddress}
                onChange={(e) => handleInputChange('billingProviderAddress', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="City"
                fullWidth
                value={form.billingProviderCity}
                onChange={(e) => handleInputChange('billingProviderCity', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="State"
                fullWidth
                value={form.billingProviderState}
                onChange={(e) => handleInputChange('billingProviderState', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="ZIP"
                fullWidth
                value={form.billingProviderZip}
                onChange={(e) => handleInputChange('billingProviderZip', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box className={classes.formSection}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Payer Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Payer Name"
                fullWidth
                value={form.payerName}
                onChange={(e) => handleInputChange('payerName', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Policy Number"
                fullWidth
                value={form.policyNumber}
                onChange={(e) => handleInputChange('policyNumber', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box className={classes.formSection}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Patient Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Patient Name"
                fullWidth
                value={form.patientName}
                onChange={(e) => handleInputChange('patientName', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Date of Birth (MM/DD/YYYY)"
                fullWidth
                value={formatDate(form.patientDob)}
                onChange={(e) => handleInputChange('patientDob', parseDate(e.target.value))}
                margin="normal"
                variant="outlined"
                placeholder="MM/DD/YYYY"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" margin="normal" className={classes.formControl}>
                <InputLabel id="patient-sex-label">Sex</InputLabel>
                <Select
                  labelId="patient-sex-label"
                  value={form.patientSex}
                  onChange={(e) => handleInputChange('patientSex', e.target.value)}
                  label="Sex"
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                  <MenuItem value="U">Unknown</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box className={classes.formSection}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Claim Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Type of Bill"
                fullWidth
                value={form.typeOfBill}
                onChange={(e) => handleInputChange('typeOfBill', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Admission Date (MM/DD/YYYY)"
                fullWidth
                value={formatDate(form.admissionDate)}
                onChange={(e) => handleInputChange('admissionDate', parseDate(e.target.value))}
                margin="normal"
                variant="outlined"
                placeholder="MM/DD/YYYY"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Admission Type"
                fullWidth
                value={form.admissionType}
                onChange={(e) => handleInputChange('admissionType', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Admission Source"
                fullWidth
                value={form.admissionSource}
                onChange={(e) => handleInputChange('admissionSource', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Service Start Date (MM/DD/YYYY)"
                fullWidth
                value={formatDate(form.serviceStartDate)}
                onChange={(e) => handleInputChange('serviceStartDate', parseDate(e.target.value))}
                margin="normal"
                variant="outlined"
                placeholder="MM/DD/YYYY"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Service End Date (MM/DD/YYYY)"
                fullWidth
                value={formatDate(form.serviceEndDate)}
                onChange={(e) => handleInputChange('serviceEndDate', parseDate(e.target.value))}
                margin="normal"
                variant="outlined"
                placeholder="MM/DD/YYYY"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Principal Diagnosis Code"
                fullWidth
                value={form.principalDiagnosisCode}
                onChange={(e) => handleInputChange('principalDiagnosisCode', e.target.value)}
                margin="normal"
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box className={classes.formSection}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Services
          </Typography>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader}>Revenue Code</TableCell>
                  <TableCell className={classes.tableHeader}>HCPCS/Rate</TableCell>
                  <TableCell className={classes.tableHeader}>Service Date</TableCell>
                  <TableCell className={classes.tableHeader}>Service Units</TableCell>
                  <TableCell className={classes.tableHeader}>Total Charges</TableCell>
                  <TableCell className={classes.tableHeader}>Non-Covered</TableCell>
                  <TableCell className={classes.tableHeader}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.services.map((service, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        fullWidth
                        value={service.revenueCode}
                        onChange={(e) => handleServiceChange(index, 'revenueCode', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        value={service.hcpcsCode}
                        onChange={(e) => handleServiceChange(index, 'hcpcsCode', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        placeholder="MM/DD/YYYY"
                        value={formatDate(service.serviceDate)}
                        onChange={(e) => handleServiceChange(index, 'serviceDate', parseDate(e.target.value))}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={service.serviceUnits}
                        onChange={(e) => handleServiceChange(index, 'serviceUnits', Number(e.target.value))}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={service.totalCharges}
                        onChange={(e) => handleServiceChange(index, 'totalCharges', Number(e.target.value))}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={service.nonCovered}
                        onChange={(e) => handleServiceChange(index, 'nonCovered', Number(e.target.value))}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        className={classes.deleteButton}
                        onClick={() => handleDeleteService(index)}
                        disabled={form.services.length <= 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddService}
            className={classes.addButton}
          >
            Add Service
          </Button>
        </Box>

        <Box className={classes.formActions}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={!form.isDirty}
          >
            Save Form
          </Button>
          {onAddForm && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onAddForm}
              style={{ marginLeft: 8 }}
            >
              Add New Form
            </Button>
          )}
        </Box>
      </Paper>
  );
};

export default UB04FormComponent;
